/**
 * Braze Web SDK session — identity matches native (Segment identify(uuid)
 * → changeUser(uuid)). Do not changeUser(braze_id); that creates a parallel
 * Braze profile. Content Cards still subscribe only when Notification Center
 * is on; the session itself is not gated on that flag.
 */

import { AUTH_SESSION_START, LOG_OUT } from 'stash/actions';

import { BRAZE_ENV_APP_KEY, BRAZE_SDK_ENDPOINT } from 'stash/constants';

// No easy way to track this in the Braze SDK so doing it here.
let sessionIsOpen = false;
let currentUuid = null;

// ponytail: dynamic import keeps @braze/web-sdk's ~200 unbundled dev files
// (see optimizeDeps.exclude in vite.config.ts) out of every page's initial
// module graph — only fetched once a session actually starts.
let brazeSdkPromise = null;
let initializePromise = null;
/** Logout teardown (Content Cards reset + SDK destroy) before the next session. */
let brazeTeardownPromise = Promise.resolve();

const getBrazeSdk = () => {
	if (!brazeSdkPromise) {
		brazeSdkPromise = import('@braze/web-sdk');
	}
	return brazeSdkPromise;
};

const initializeBraze = () => {
	if (process.env.NODE_ENV === 'test') return Promise.resolve();
	if (initializePromise) return initializePromise;

	initializePromise = getBrazeSdk()
		.then(({ initialize }) => {
			initialize(BRAZE_ENV_APP_KEY, {
				baseUrl: BRAZE_SDK_ENDPOINT,
				enableLogging: /-/.test(window.location.hostname),
				allowUserSuppliedJavascript: true,
			});
		})
		.catch((error) => {
			initializePromise = null;
			console.warn(error);
			throw error;
		});

	return initializePromise;
};

const destroyBrazeInstance = () => {
	sessionIsOpen = false;
	currentUuid = null;
	initializePromise = null;
	brazeTeardownPromise = Promise.all([
		// Lazy: avoid pulling Content Cards mapping into every session when the
		// notification-center flag is off.
		import('stash/components/notification-center/contentCardsService')
			.then(({ resetContentCardsService }) => {
				resetContentCardsService();
			})
			.catch(console.warn),
		getBrazeSdk()
			.then(({ removeAllSubscriptions, destroy }) => {
				removeAllSubscriptions();
				destroy();
			})
			.catch(console.warn),
	]).then(() => undefined);
};

const setBrazeUser = (uuid) => {
	if (!uuid || uuid === currentUuid) return;

	// A different uuid can arrive without an intervening LOG_OUT (e.g. a
	// second LOG_IN); re-identify Braze to the new user instead of staying
	// stuck on whoever the session was originally opened for.
	const isIdentityChange = sessionIsOpen;
	sessionIsOpen = true;
	currentUuid = uuid;

	brazeTeardownPromise
		.then(() => initializeBraze())
		.then(() => getBrazeSdk())
		.then((braze) => {
			// A LOG_OUT can land while this import is in flight and already
			// flip sessionIsOpen back to false via destroyBrazeInstance; bail
			// out instead of reopening a session right after teardown.
			if (!sessionIsOpen) return;

			const { automaticallyShowInAppMessages, changeUser, openSession } = braze;

			changeUser(uuid);
			if (isIdentityChange) {
				// Cached cards + read/dismissed state belong to the previous
				// user; clear them so the new user doesn't see stale cards.
				import('stash/components/notification-center/contentCardsService')
					.then(({ resetContentCardsService }) => resetContentCardsService())
					.catch(console.warn);
			} else {
				automaticallyShowInAppMessages();
				openSession();
			}
		})
		.catch((error) => {
			sessionIsOpen = false;
			currentUuid = null;
			console.warn(error);
		});
};

export const logBrazeCustomEvent = (eventName, properties) => {
	if (!eventName) return;

	getBrazeSdk()
		.then(({ logCustomEvent }) => {
			logCustomEvent(eventName, properties);
		})
		.catch(console.warn);
};

const brazeReducer = () => (next) => (action) => {
	switch (action.type) {
		case AUTH_SESSION_START:
			setBrazeUser(action.uuid);
			break;
		case LOG_OUT:
			destroyBrazeInstance();
			break;
		default:
			break;
	}

	return next(action);
};

export default brazeReducer;
